import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { CHECKOUT_STEP_MAP, GIFT_OPTIONS_STEP, REVIEW_STEP, SHIP_OPTIONS_STEP } from 'constants/checkoutFlow';

import { trackEvent } from 'helpers/analytics';
import { changeItemQuantity } from 'actions/checkout/review';
import { setGiftOptionsState } from 'actions/checkout/giftOptions';
import {
  advanceCheckout,
  fetchCheckoutData,
  setIsLoadingReviewSection,
  setShouldDefaultOptions,
  updateCheckoutForTargetStep
} from 'actions/checkout/purchase';
import CondensedItems from 'components/checkout/caap/spc/CondensedItems';
import ReviewItems from 'components/checkout/caap/spc/ReviewItems';
import StepSection from 'components/checkout/caap/StepSection';
import { getPathFromStep } from 'helpers/CheckoutFlowControl';
import {
  hasGcQuantity,
  hasInvalidGiftOptions,
  isChangingPayment,
  isChangingShipping,
  isReadyToSubmit,
  isReviewStep
} from 'helpers/CheckoutUtils';

export class ReviewSection extends Component {
  onChangeQuantity = (e, lineItemId) => {
    const { setIsLoadingReviewSection, changeItemQuantity, checkoutData: { purchaseData: { isFetchingCheckoutData } } } = this.props;
    const quantity = e.target.value;

    trackEvent('TE_CHECKOUT_REV_QTY', `${lineItemId}:${quantity}`);
    if (!isFetchingCheckoutData) {
      setIsLoadingReviewSection(true);
      changeItemQuantity({ lineItemId, quantity });
    }
  }

  onToggleShouldDefaultOptionsClick = () => {
    const { checkoutData: { purchaseData: { shouldDefaultOptions } }, setShouldDefaultOptions } = this.props;
    setShouldDefaultOptions(!shouldDefaultOptions);
  }

  onViewGiftOptionsClick = (e, { lineItemId, giftMessage, giftWrapId }) => {
    e.preventDefault();
    const { purchaseData: { data: { nameOnAccount } } } = this.props.checkoutData;
    const defaultMessage = `Hi,\nEnjoy your gift!\nFrom ${nameOnAccount}`;
    const displayedMessage = giftMessage === null ? defaultMessage : giftMessage;
    const { setGiftOptionsState, updateCheckoutForTargetStep } = this.props;

    trackEvent('TE_CHECKOUT_SPC_VIEW_GIFT_OPTIONS');
    setGiftOptionsState({
      giftMessage: displayedMessage,
      useGiftReceipt: true,
      useGiftBag: !!giftWrapId,
      lineItemId
    });
    updateCheckoutForTargetStep({ targetStep: GIFT_OPTIONS_STEP, newUrl: getPathFromStep(GIFT_OPTIONS_STEP) });
  }

  onViewLinkClick = e => {
    e.preventDefault();
    const { updateCheckoutForTargetStep } = this.props;
    trackEvent('TE_CHECKOUT_SPC_VIEW_REVIEWS');
    updateCheckoutForTargetStep({ targetStep: REVIEW_STEP, newUrl: getPathFromStep(REVIEW_STEP) });
  }

  render() {
    const {
      addresses,
      paymentTypes,
      shipOptions,
      purchaseData: {
        asinErrors,
        constraintViolations: cvs,
        doesPurchaseRequireCC,
        isFetchingCheckoutData,
        isLoadingReviewSection,
        isNewCustomer,
        productsByLineItem,
        showShouldDefaultOptions,
        shouldDefaultOptions,
        targetStep,
        data: { constraintViolations, eligibleForGiftWrap, purchaseId }
      }
    } = this.props.checkoutData;

    const { excludeShipOptions, reviewStepNumber } = this.props;

    const { testId } = this.context;

    const {
      byLineItem,
      groups = {}
    } = shipOptions;

    const {
      saved: savedAddresses = []
    } = addresses;

    const {
      saved: savedPayments
    } = paymentTypes;

    const isInvalidGiftOption = hasInvalidGiftOptions(cvs);
    const showShipOptionsModal = targetStep === SHIP_OPTIONS_STEP;
    const showReviewStep = isReviewStep(targetStep);
    const showImb = showShouldDefaultOptions && showReviewStep && !showShipOptionsModal;
    const isChangeShipping = isChangingShipping(targetStep);
    const isChangePayment = isChangingPayment(targetStep);
    const hasInlinePaymentForm = !(savedPayments && savedPayments.length);
    const hasInlineAddressForm = !!isNewCustomer;
    const nonReviewStep = isChangeShipping || isChangePayment;
    const nonReviewStepWithoutCV = !constraintViolations.length && nonReviewStep;
    const showCondensedReviews = nonReviewStep
    || (hasInlinePaymentForm && doesPurchaseRequireCC)
    || (hasInlineAddressForm && !savedAddresses.length)
    || (excludeShipOptions && showShipOptionsModal);

    const showViewLink = nonReviewStepWithoutCV
      && !hasInlinePaymentForm
      && !(hasInlineAddressForm && !savedAddresses.length)
      && !showShipOptionsModal;

    const sectionTitle = excludeShipOptions ? 'Review order' : 'Review items and shipping';
    const isOrderReadyToSubmit = isReadyToSubmit(this.props.checkoutData);
    const hasGcQuantityCv = hasGcQuantity(cvs);

    return (
      <StepSection
        addOverlay={isLoadingReviewSection}
        isCurrentStep={showReviewStep && !(excludeShipOptions && showShipOptionsModal)}
        linkText="View"
        linkTo={`${CHECKOUT_STEP_MAP[REVIEW_STEP]}?pid=${purchaseId}`}
        onLinkClick={this.onViewLinkClick}
        showLink={showViewLink}
        showTitleBar={true}
        stepNumber={reviewStepNumber}
        title={sectionTitle}
        dataTestIdLoader={`${testId('reviewSpinner')}`}>
        {
          showCondensedReviews
            ? <CondensedItems
              byLineItem={byLineItem}
              productsByLineItem={productsByLineItem} />
            : <ReviewItems
              asinErrors={asinErrors}
              hasGcQuantityCv={hasGcQuantityCv}
              hideReviewItems={showShipOptionsModal}
              eligibleForGiftWrap={eligibleForGiftWrap}
              isFetchingCheckoutData={isFetchingCheckoutData}
              byLineItem={byLineItem}
              productsByLineItem={productsByLineItem}
              groups={groups}
              onChangeQuantity={this.onChangeQuantity}
              onViewGiftOptionsClick={this.onViewGiftOptionsClick}
              onToggleShouldDefaultOptionsClick={this.onToggleShouldDefaultOptionsClick}
              shouldDefaultOptions={shouldDefaultOptions}
              showImb={showImb}
              excludeShipOptions={excludeShipOptions}
              isOrderReadyToSubmit={isOrderReadyToSubmit}
              isInvalidGiftOption={isInvalidGiftOption} />
        }
      </StepSection>
    );
  }
}

ReviewSection.contextTypes = {
  testId: PropTypes.func
};

function mapStateToProps(state) {
  return {
    checkoutData: state.checkoutData
  };
}

export default connect(mapStateToProps, {
  advanceCheckout,
  changeItemQuantity,
  fetchCheckoutData,
  setGiftOptionsState,
  setShouldDefaultOptions,
  setIsLoadingReviewSection,
  updateCheckoutForTargetStep
})(ReviewSection);
